import React from 'react';
import { useTranslation } from 'react-i18next';
import { getBrowserLocale } from '../../../utils/language';

function getStatusClasses(status) {
  const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';
  if (normalizedStatus === 'upgrade_scheduled') {
    return 't-bg-indigo-50 t-border-indigo-200 t-text-indigo-700';
  }
  if (normalizedStatus === 'active') {
    return 't-bg-emerald-50 t-border-emerald-200 t-text-emerald-700';
  }
  if (normalizedStatus === 'trialing' || normalizedStatus === 'paused') {
    return 't-bg-amber-50 t-border-amber-200 t-text-amber-700';
  }
  if (normalizedStatus === 'canceled' || normalizedStatus === 'cancelled') {
    return 't-bg-rose-50 t-border-rose-200 t-text-rose-700';
  }
  return 't-bg-slate-100 t-border-slate-200 t-text-slate-600';
}

function getPausedUntilLabelForSub(sub) {
  const resumesAt = sub?.stripeData?.pause_collection?.resumes_at;
  let dateValue = null;
  if (typeof resumesAt === 'number') {
    dateValue = new Date(resumesAt * 1000);
  } else if (sub?.pausedUntil) {
    dateValue = new Date(sub.pausedUntil);
  }
  if (!dateValue || Number.isNaN(dateValue.getTime())) return null;
  return dateValue.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function isManageable(sub) {
  const duration = String(sub?.duration ?? '');
  const productType = String(sub?.product_type ?? '').toLowerCase();
  const status = (sub?.stripeData?.status ?? sub?.status ?? '').toLowerCase();
  const isOneOff = ['30', '90'].includes(duration);
  const activeItems = Array.isArray(sub?.items)
    ? sub.items.filter((item) => (item?.status ?? '').toLowerCase() === 'active')
    : [];

  // One-off rows are only manageable when the user can remove an extra active item.
  if (isOneOff) {
    return activeItems.length > 1;
  }

  // Hide Manage for cancelled recurring rows.
  if (status === 'canceled' || status === 'cancelled') {
    return false;
  }

  if (productType === 'omega-3') {
    return ['active', 'trialing', 'paused'].includes(status);
  }

  return ['active', 'trialing', 'paused'].includes(status);
}

function getStatusLabel({ effectiveStatus, statusForBadge, t, hasPendingUpgrade }) {
  if (hasPendingUpgrade) {
    return 'Upgrade scheduled';
  }
  if (effectiveStatus === 'trialing' || effectiveStatus === 'paused') {
    return t('CustomerSubs_PausedBadge');
  }
  if (effectiveStatus === 'active') {
    return t('CustomerSubs_Status_Active');
  }
  if (effectiveStatus === 'canceled' || effectiveStatus === 'cancelled') {
    return t('CustomerSubs_Status_Canceled');
  }
  return statusForBadge || '-';
}

function formatUpcomingInvoice(sub, locale, t) {
  if (!sub?.stripeData?.upcoming_invoice?.next_payment_date) {
    return '-';
  }

  return `${new Date(sub.stripeData.upcoming_invoice.next_payment_date).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })} ${t('CustomerSubs_ForAmount', {
    amount: sub.stripeData.upcoming_invoice.amount_due,
  })}`;
}

function formatPendingChangeDate(sub, locale, pendingChange = sub?.pendingPlanChange) {
  const dateValue = pendingChange?.effectiveAt;
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPendingChangeSummary(sub, pendingChangeDate, t) {
  if (!pendingChangeDate) return null;
  const fromLabel = sub?.pendingPlanChange?.fromLabel;
  const toLabel = sub?.pendingPlanChange?.toLabel;
  const targetAmount = Number(sub?.pendingPlanChange?.targetAmount);
  const toCadenceMonths = Number(sub?.pendingPlanChange?.toCadenceMonths || 0);
  const savingsPercent = Number(sub?.pendingPlanChange?.savingsPercent);

  const cadenceLabel = toCadenceMonths > 0 ? toCadenceMonths : 1;
  const hasTargetPrice = Number.isFinite(targetAmount);
  const hasSaving = Number.isFinite(savingsPercent) && savingsPercent > 0;

  if (fromLabel && toLabel && hasTargetPrice && hasSaving) {
    return t('CustomerSubs_PendingPlanChange_WithPriceAndSavings', {
      from: fromLabel,
      to: toLabel,
      date: pendingChangeDate,
      amount: targetAmount.toFixed(2),
      cadence: cadenceLabel,
      savings: Math.round(savingsPercent),
    });
  }
  if (fromLabel && toLabel && hasTargetPrice) {
    return t('CustomerSubs_PendingPlanChange_WithPrice', {
      from: fromLabel,
      to: toLabel,
      date: pendingChangeDate,
      amount: targetAmount.toFixed(2),
      cadence: cadenceLabel,
    });
  }
  if (fromLabel && toLabel) {
    return t('CustomerSubs_PendingPlanChange_Basic', {
      from: fromLabel,
      to: toLabel,
      date: pendingChangeDate,
    });
  }
  return t('CustomerSubs_PendingPlanChange_Fallback', { date: pendingChangeDate });
}

export default function SubscriptionsStatusTable({
  subscriptionsList = [],
  selectedLang,
  onManage,
  selectedSubId,
}) {
  const { t } = useTranslation();

  const getProductLabel = (sub) => {
    if (['30', '90'].includes(String(sub?.duration))) {
      return String(sub?.product_type || '').toLowerCase() === 'omega-3'
        ? t('CustomerSubs_OneOff_Omega3')
        : t('CustomerSubs_OneOff_Riise');
    }
    const productName = sub?.stripeData?.product ?? sub?.title;

    if (productName?.includes('3-month')) {
      return t('CustomerSubs_Product_3Month');
    }
    if (productName?.includes('monthly')) {
      return t('CustomerSubs_Product_Monthly');
    }

    return productName;
  };

  const getBillingFrequencyLabel = (sub) => {
    const duration = String(sub?.duration ?? '');
    if (duration === '30' || duration === '90') return '-';

    const productType = sub?.product_type;
    if (productType === 'omega-3') {
      if (duration === '1') return t('CustomerSubs_Billing_Monthly');
      if (duration === '3') return t('CustomerSubs_Billing_ThreeMonthly');
      return '-';
    }

    if (duration === '3') return t('CustomerSubs_Billing_Monthly');
    if (duration === '6') return t('CustomerSubs_Billing_ThreeMonthly');
    return '-';
  };

  const locale = getBrowserLocale(selectedLang);

  if (!subscriptionsList.length) {
    return null;
  }

  return (
    <div className="t-grid t-gap-4 xl:t-grid-cols-2">
      {subscriptionsList.map((sub) => {
        const stripeStatus = sub?.stripeData?.status;
        const effectiveStatus = (stripeStatus ?? sub?.status ?? '').toLowerCase();
        const statusForBadge = stripeStatus ?? sub?.status;
        const isRowOneOff = ['30', '90'].includes(String(sub?.duration ?? ''));
        const pausedRowLabel = getPausedUntilLabelForSub(sub);
        const isSelected = selectedSubId === sub.id;
        const canManage = isManageable(sub);
        const activeItems =
          sub?.items?.filter((item) => (item.status ?? '').toLowerCase() === 'active') || [];
        const pendingChangeDate = formatPendingChangeDate(sub, locale);
        const hasPlanChangeMetadata =
          String(sub?.stripeData?.metadata?.plan_change_from || '').length > 0;
        const isPausedState =
          effectiveStatus === 'paused' ||
          effectiveStatus === 'trialing' ||
          String(sub?.status ?? '').toLowerCase() === 'paused';
        const hasPendingUpgrade =
          !isPausedState &&
          (Boolean(pendingChangeDate) ||
            (String(sub?.stripeData?.status || '').toLowerCase() === 'trialing' && hasPlanChangeMetadata));
        const statusClassKey = hasPendingUpgrade ? 'upgrade_scheduled' : effectiveStatus;
        const pendingChangeSummary = formatPendingChangeSummary(sub, pendingChangeDate, t);
        const pendingFormulaUpdate = sub?.pendingFormulaUpdate?.type === 'formula'
          ? sub.pendingFormulaUpdate : null;
        const pendingFormulaDate = formatPendingChangeDate(sub, locale, pendingFormulaUpdate);
        const formulaAmount = Number(pendingFormulaUpdate?.amount);
        const pendingFormulaPrice = Number.isFinite(formulaAmount) && formulaAmount > 0
          ? new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: sub?.stripeData?.currency || 'EUR',
            }).format(formulaAmount)
          : null;

        return (
          <article
            key={sub.id}
            className={`t-relative t-overflow-hidden t-rounded-lg t-border t-bg-white t-p-3.5 md:t-p-5 t-shadow-[0_8px_20px_rgba(15,23,42,0.05)] t-transition-all ${
              isSelected
                ? 't-border-secondary/60 t-ring-2 t-ring-secondary/10'
                : 't-border-slate-200 hover:t-border-slate-300'
            }`}
          >
            <div className="t-flex t-items-start t-justify-between t-gap-4">
              <div className="t-min-w-0 t-flex-1 t-space-y-3">
                <div className="t-flex t-flex-wrap t-items-center t-gap-2">
                  <h3 className="t-m-0 t-text-lg t-font-semibold t-leading-6 t-text-slate-900">
                    {getProductLabel(sub)}
                  </h3>
                  {!isRowOneOff && (
                    <span
                      className={`t-inline-flex t-items-center t-rounded-full t-border t-px-3 t-py-1 t-text-xs t-font-semibold ${getStatusClasses(
                        statusClassKey,
                      )}`}
                    >
                      {getStatusLabel({ effectiveStatus, statusForBadge, t, hasPendingUpgrade })}
                    </span>
                  )}
                </div>

                {!!sub?.stripeData?.pause_collection && pausedRowLabel && (
                  <div className="t-inline-flex t-items-center t-gap-2 t-rounded-full t-bg-slate-100 t-px-3 t-py-1.5 t-text-xs t-font-medium t-text-slate-600">
                    <span aria-hidden="true">⏸</span>
                    {t('CustomerSubs_CollectionPausedUntil', { date: pausedRowLabel })}
                  </div>
                )}
              </div>

              {onManage && canManage && (
                <button
                  type="button"
                  onClick={() => onManage(sub)}
                  className={`t-inline-flex t-flex-shrink-0 t-items-center t-justify-center t-rounded-full t-border t-px-2 t-py-1 t-text-sm t-font-semibold t-transition-colors ${
                    isSelected
                      ? 't-border-secondary t-bg-secondary t-text-white'
                      : 't-border-secondary t-bg-white t-text-secondary hover:t-bg-teal-50'
                  }`}
                >
                  {t('CustomerSubs_Table_Manage')}
                </button>
              )}
            </div>

            {pendingChangeDate && (
              <div className="t-mt-4 t-flex t-w-full t-items-start t-gap-2 t-rounded-lg t-bg-indigo-50 t-px-3 t-py-1.5 t-text-xs t-font-medium t-text-indigo-800 lg:t-text-sm">
                <span aria-hidden="true" className="t-text-xl -t-mt-2">↻</span>
                {pendingChangeSummary}
              </div>
            )}

            {pendingFormulaDate && (
              <div role="status" className="t-mt-4 t-w-full t-rounded-lg t-bg-indigo-50 t-px-3 t-py-2 t-text-xs t-text-indigo-800 lg:t-text-sm">
                <p className="t-m-0 t-font-semibold">
                  {t('CustomerSubs_FormulaUpdateScheduled')}
                </p>
                {pendingFormulaPrice && (
                  <p className="t-m-0 t-mt-1">
                    {t(String(sub.duration) === '6'
                      ? 'CustomerSubs_FormulaUpdatePrice_Quarterly'
                      : 'CustomerSubs_FormulaUpdatePrice_Monthly', { amount: pendingFormulaPrice })}
                  </p>
                )}
                <p className="t-m-0 t-mt-1">
                  {t('CustomerSubs_FormulaUpdateEffectiveDate', { date: pendingFormulaDate })}
                </p>
              </div>
            )}

            <div className="t-mt-4 t-grid t-grid-cols-1 t-gap-2.5 sm:t-grid-cols-2">
              <div className="t-rounded-lg t-bg-slate-50 t-p-3.5">
                <p className="t-m-0 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.08em] t-text-slate-500">
                  {t('CustomerSubs_Table_Frequency')}
                </p>
                <p className="t-mb-0 t-mt-1 t-text-sm t-font-medium t-text-slate-800">
                  {getBillingFrequencyLabel(sub)}
                </p>
              </div>

              <div className="t-rounded-lg t-bg-slate-50 t-p-3.5">
                <p className="t-m-0 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.08em] t-text-slate-500">
                  {t('CustomerSubs_Table_NextInvoice')}
                </p>
                <p className="t-mb-0 t-mt-1 t-text-sm t-leading-5 t-text-slate-800">
                  {formatUpcomingInvoice(sub, locale, t)}
                </p>
              </div>
            </div>

            <div className="t-mt-3.5 t-flex t-items-center t-gap-3 t-border-t t-border-slate-100 t-pt-3">
              <div className="t-flex t-items-center t-gap-2 t-flex-shrink-0">
                <p className="t-m-0 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.08em] t-text-slate-700">
                  {t('CustomerSubs_Table_Items')}
                </p>
              </div>

              {activeItems.length > 0 ? (
                <div className="t-flex t-flex-wrap t-gap-1.5">
                  {activeItems.map((item) => (
                    <span
                      key={item.id}
                      className="t-inline-flex t-items-center t-rounded-md t-bg-slate-100 t-px-2.5 t-py-1 t-text-[11px] t-font-medium t-text-slate-700 t-capitalize"
                    >
                      {item.product_name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="t-m-0 t-text-sm t-text-slate-400">-</p>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
